// ─────────────────────────────────────────────────────────────
//  Einmalzahlungen am Kunden (Setup-Fee, Beratung, Workshop …)
//
//  Gepflegt unter customers/{slug}/oneTimes/{id}. Jede Einmalzahlung erzeugt
//  im Zahlungen-Dashboard genau EINE Rechnung (deterministische ID
//  `slug__once_<id>`), sobald ihr Fälligkeitsdatum erreicht ist.
//
//  Provisionen: je Zahlung kann pro Teammitglied ein Anteil hinterlegt werden –
//  PROZENTUAL vom Netto oder als FIXER Betrag. Ausgezahlt wird wie überall erst,
//  wenn der Kunde die Rechnung bezahlt hat.
//
//  Die Rechenlogik liegt vollständig in billingCore.js; diese Komponente ist
//  reine Eingabe/Anzeige.
// ─────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import {
  oneTimeList,
  oneTimeCommissionFor,
  oneTimePayoutTotal,
  taxBreakdown,
  taxModeInfo,
} from './paymentEngine';
import { MAX_FEATURES, packageList } from './setupPackagesCore';
import './OneTimePayments.css';

const fmtEur = (n) =>
  !isFinite(n) ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' €';
const fmtDay = (ms) =>
  !ms ? '–' : new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const toDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
// Mittags verankern, damit Zeitzonen den Fälligkeitstag nicht verschieben.
const fromDateInput = (v) => (v ? new Date(`${v}T09:00:00`).getTime() : 0);

const parseNum = (v) => {
  const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return isFinite(n) ? n : 0;
};

const newOneTimeId = () => `ot${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

const emptyForm = () => ({
  id: null,
  label: '',
  amount: '',
  dueDate: toDateInput(Date.now()),
  note: '',
  features: [],
  commissions: {},
});

export default function OneTimePayments({ customer, team, packages: packagesRaw, onPatch }) {
  const [form, setForm] = useState(null);

  // Setup-Pakete aus den Settings – reine Startwerte für neue Positionen.
  const packages = useMemo(() => packageList(packagesRaw, true), [packagesRaw]);

  const contract = customer.contract || null;
  const contractSetupFee = Math.max(0, Math.round(Number(contract && contract.setupFee) || 0));
  const taxInfo = taxModeInfo(contract && contract.taxMode);
  const items = useMemo(() => oneTimeList(customer), [customer]);

  // Auswahl für die Provisionen: aktive Mitglieder plus alle, die an einer
  // bestehenden Zahlung bereits beteiligt sind (auch wenn sie inzwischen
  // deaktiviert wurden – sonst verschwände ihr Anteil unsichtbar).
  const memberOptions = useMemo(() => {
    const involved = new Set();
    items.forEach((o) => Object.keys(o.commissions).forEach((id) => involved.add(id)));
    return Object.keys(team || {})
      .filter((id) => team[id] && (team[id].active !== false || involved.has(id)))
      .map((id) => ({ id, ...team[id] }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [team, items]);

  const memberName = (id) => (team && team[id] && team[id].name) || id;

  const openNew = () => setForm(emptyForm());
  const openEdit = (o) => setForm({
    id: o.id,
    label: o.label,
    amount: String(Math.round(o.amount)),
    dueDate: toDateInput(o.dueDate),
    note: o.note || '',
    features: [...(o.features || [])],
    commissions: Object.keys(o.commissions).reduce((acc, mid) => {
      acc[mid] = { mode: o.commissions[mid].mode, value: String(o.commissions[mid].value) };
      return acc;
    }, {}),
  });
  const closeForm = () => setForm(null);

  // Paket übernehmen: füllt Bezeichnung, Preis, Text und Features. Provisionen
  // und Fälligkeit bleiben unangetastet – die sind pro Kunde verschieden.
  const applyPackage = (p) => setForm((f) => ({
    ...(f || emptyForm()),
    label: p.label,
    amount: p.amount > 0 ? String(p.amount) : '',
    note: p.description,
    features: [...p.features],
  }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const toggleMember = (mid) => setForm((f) => {
    const next = { ...f.commissions };
    if (next[mid]) delete next[mid];
    else next[mid] = { mode: 'percent', value: '' };
    return { ...f, commissions: next };
  });
  const setCommission = (mid, patch) => setForm((f) => ({
    ...f,
    commissions: { ...f.commissions, [mid]: { ...f.commissions[mid], ...patch } },
  }));

  // Live-Vorschau des Formulars – rechnet mit exakt derselben Logik wie später
  // die Abrechnung, indem ein Entwurf in die Core-Form gebracht wird.
  const draft = useMemo(() => {
    if (!form) return null;
    const amount = Math.max(0, Math.round(parseNum(form.amount)));
    const commissions = {};
    Object.keys(form.commissions).forEach((mid) => {
      const c = form.commissions[mid];
      const value = parseNum(c.value);
      if (value > 0) commissions[mid] = { mode: c.mode, value };
    });
    const entry = { id: form.id || 'draft', amount, commissions };
    const shares = Object.keys(commissions).map((mid) => ({
      memberId: mid,
      ...oneTimeCommissionFor(entry, mid),
    })).filter((s) => s.amount > 0);
    const payoutTotal = oneTimePayoutTotal(entry);
    return { amount, tax: taxBreakdown(amount, contract), shares, payoutTotal, remaining: amount - payoutTotal };
  }, [form, contract]);

  const canSave = !!(form && form.label.trim() && draft && draft.amount > 0 && form.dueDate);

  const save = () => {
    if (!canSave) return;
    const id = form.id || newOneTimeId();
    const features = form.features.map((f) => f.trim()).filter(Boolean).slice(0, MAX_FEATURES);
    const rec = {
      label: form.label.trim().slice(0, 120),
      amount: draft.amount,
      dueDate: fromDateInput(form.dueDate),
      note: form.note.trim().slice(0, 500) || null,
      features: features.length ? features : null,
      updatedAt: Date.now(),
    };
    // Der Patch ersetzt den Knoten komplett – Storno-Flag und Anlagedatum
    // müssen deshalb aktiv übernommen werden, sonst gehen sie beim Bearbeiten verloren.
    const prev = form.id ? (customer.oneTimes || {})[form.id] : null;
    rec.createdAt = (prev && Number(prev.createdAt)) || Date.now();
    if (prev && prev.cancelled) rec.cancelled = true;
    const commissions = {};
    Object.keys(form.commissions).forEach((mid) => {
      const value = parseNum(form.commissions[mid].value);
      if (value > 0) commissions[mid] = { mode: form.commissions[mid].mode, value };
    });
    // Provisionen als Ganzes ersetzen: entfernte Mitglieder müssen verschwinden.
    rec.commissions = Object.keys(commissions).length ? commissions : null;
    onPatch({ [`oneTimes/${id}`]: rec });
    closeForm();
  };

  const setCancelled = (o, cancelled) =>
    onPatch({ [`oneTimes/${o.id}/cancelled`]: cancelled ? true : null });

  const removeItem = (o) => {
    if (!window.confirm(`Einmalzahlung „${o.label}" über ${fmtEur(o.amount)} wirklich löschen?\n\nEine bereits erzeugte Rechnung bleibt bestehen und muss im Zahlungen-Dashboard storniert werden.`)) return;
    onPatch({ [`oneTimes/${o.id}`]: null });
  };

  const activeTotal = items.filter((o) => !o.cancelled).reduce((s, o) => s + o.amount, 0);

  return (
    <div className="ot-box">
      <div className="ot-head">
        <span>Einmalzahlungen</span>
        <div className="ot-head-right">
          {activeTotal > 0 && <span className="ot-head-sum">{fmtEur(activeTotal)} netto</span>}
          {!form && <button type="button" className="ot-btn ot-btn-primary" onClick={openNew}>+ Hinzufügen</button>}
        </div>
      </div>

      {/* Die Setup-Gebühr aus dem Vertrag erzeugt bereits eine eigene Rechnung.
          Wer sie hier nochmal anlegt, kassiert doppelt – einmal deutlich sagen. */}
      {contractSetupFee > 0 && (
        <p className="ot-warn ot-warn-soft">
          Im Vertrag ist bereits eine Setup-Gebühr von <strong>{fmtEur(contractSetupFee)}</strong> netto hinterlegt.
          Die wird beim Vertragsstart automatisch abgerechnet – leg sie hier nicht zusätzlich an, sonst zahlt der Kunde doppelt.
        </p>
      )}

      {!items.length && !form && (
        <p className="ot-intro">
          Setup-Fee, Beratung, Workshop – alles, was einmalig abgerechnet wird. Jede Position
          erzeugt am Fälligkeitstag automatisch eine eigene Rechnung und kann Provisionen
          fürs Team enthalten (prozentual oder als Fixbetrag).
        </p>
      )}

      {!!items.length && (
        <ul className="ot-list">
          {items.map((o) => {
            const tax = taxBreakdown(o.amount, contract);
            const shares = Object.keys(o.commissions)
              .map((mid) => ({ memberId: mid, ...oneTimeCommissionFor(o, mid) }))
              .filter((s) => s.amount > 0);
            const payout = oneTimePayoutTotal(o);
            const billed = !!o.dueDate && o.dueDate <= Date.now();
            return (
              <li key={o.id} className={`ot-item ${o.cancelled ? 'is-cancelled' : ''}`}>
                <div className="ot-item-main">
                  <div className="ot-item-title">
                    <span className="ot-item-label">{o.label}</span>
                    {o.cancelled
                      ? <span className="ot-tag ot-tag-off">storniert</span>
                      : billed
                        ? <span className="ot-tag ot-tag-on">abgerechnet</span>
                        : <span className="ot-tag">geplant</span>}
                  </div>
                  <div className="ot-item-meta">
                    <span>{fmtEur(o.amount)} netto</span>
                    {tax.rate > 0 && <span>· {fmtEur(tax.gross)} brutto</span>}
                    <span>· fällig {fmtDay(o.dueDate)}</span>
                  </div>
                  {o.note && <p className="ot-item-note">{o.note}</p>}
                  {!!o.features.length && (
                    <ul className="ot-item-features">
                      {o.features.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}
                  {!!shares.length && (
                    <div className="ot-shares">
                      {shares.map((s) => (
                        <span key={s.memberId} className="ot-share">
                          {memberName(s.memberId)}
                          <em>{s.mode === 'percent' ? `${s.value} %` : 'fix'}</em>
                          <strong>{fmtEur(s.amount)}</strong>
                        </span>
                      ))}
                      <span className="ot-share ot-share-rest">
                        bleibt<strong>{fmtEur(o.amount - payout)}</strong>
                      </span>
                    </div>
                  )}
                </div>
                <div className="ot-item-actions">
                  <button type="button" className="ot-btn ot-btn-ghost" onClick={() => openEdit(o)}>Bearbeiten</button>
                  {o.cancelled
                    ? <button type="button" className="ot-btn ot-btn-ghost" onClick={() => setCancelled(o, false)}>Aktivieren</button>
                    : <button type="button" className="ot-btn ot-btn-ghost" onClick={() => setCancelled(o, true)}>Stornieren</button>}
                  <button type="button" className="ot-btn ot-btn-ghost ot-btn-del" onClick={() => removeItem(o)} title="Löschen">✕</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {form && (
        <div className="ot-form">
          {!!packages.length && (
            <div className="ot-packs">
              <div className="ot-packs-head">
                <span>Setup-Paket übernehmen</span>
                <span className="ot-packs-hint">füllt Bezeichnung, Preis und Features</span>
              </div>
              <div className="ot-packs-list">
                {packages.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="ot-pack"
                    title={p.description || p.label}
                    onClick={() => applyPackage(p)}
                  >
                    <span className="ot-pack-name">{p.label}</span>
                    <span className="ot-pack-price">{fmtEur(p.amount)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="ot-form-row">
            <label className="ot-field ot-field-wide">
              <span>Bezeichnung</span>
              <input
                type="text"
                value={form.label}
                placeholder="z. B. Setup-Fee, Beratung Q1, Workshop"
                onChange={(e) => setField('label', e.target.value)}
              />
            </label>
          </div>
          <div className="ot-form-row">
            <label className="ot-field">
              <span>Betrag netto (€)</span>
              <input type="text" inputMode="numeric" value={form.amount} placeholder="0" onChange={(e) => setField('amount', e.target.value)} />
            </label>
            <label className="ot-field">
              <span>Fällig am</span>
              <input type="date" value={form.dueDate} onChange={(e) => setField('dueDate', e.target.value)} />
            </label>
          </div>

          <label className="ot-field ot-field-wide">
            <span>Beschreibung (optional)</span>
            <textarea
              rows={2}
              value={form.note}
              maxLength={500}
              placeholder="Was genau ist vereinbart?"
              onChange={(e) => setField('note', e.target.value)}
            />
          </label>

          {!!form.features.length && (
            <div className="ot-feats">
              <span className="ot-feats-head">Enthaltene Leistungen</span>
              <div className="ot-feats-list">
                {form.features.map((f, i) => (
                  <span key={i} className="ot-feat">
                    {f}
                    <button type="button" aria-label="Entfernen" onClick={() => removeFeature(i)}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="ot-comm">
            <div className="ot-comm-head">
              <span>Provisionen fürs Team</span>
              <span className="ot-comm-hint">prozentual vom Netto oder fixer Betrag</span>
            </div>
            {!memberOptions.length && <p className="ot-empty">Noch keine Teammitglieder angelegt.</p>}
            {memberOptions.map((m) => {
              const on = !!form.commissions[m.id];
              const c = form.commissions[m.id];
              const share = on ? draft.shares.find((s) => s.memberId === m.id) : null;
              return (
                <div key={m.id} className={`ot-comm-row ${on ? 'is-on' : ''}`}>
                  <button
                    type="button"
                    className="ot-comm-name"
                    role="switch"
                    aria-checked={on}
                    onClick={() => toggleMember(m.id)}
                  >
                    <span className="ot-comm-check" aria-hidden="true">{on ? '✓' : ''}</span>
                    <span>{m.name || m.id}</span>
                    {m.role && <em>{m.role}</em>}
                  </button>
                  {on && (
                    <div className="ot-comm-input">
                      <div className="ot-mode">
                        <button
                          type="button"
                          className={c.mode === 'percent' ? 'is-on' : ''}
                          onClick={() => setCommission(m.id, { mode: 'percent' })}
                        >%</button>
                        <button
                          type="button"
                          className={c.mode === 'fixed' ? 'is-on' : ''}
                          onClick={() => setCommission(m.id, { mode: 'fixed' })}
                        >€</button>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={c.value}
                        placeholder={c.mode === 'percent' ? 'z. B. 10' : 'z. B. 250'}
                        onChange={(e) => setCommission(m.id, { value: e.target.value })}
                      />
                      <span className="ot-comm-result">{share ? fmtEur(share.amount) : '—'}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="ot-preview">
            <div className="ot-preview-row">
              <span>Kunde zahlt</span>
              <span>
                {fmtEur(draft.amount)} netto
                {draft.tax.rate > 0 ? ` + ${draft.tax.rate}% IVA = ${fmtEur(draft.tax.gross)} brutto` : ` = ${fmtEur(draft.tax.gross)} brutto`}
              </span>
            </div>
            <div className="ot-preview-row">
              <span>Provisionen gesamt</span>
              <span>{fmtEur(draft.payoutTotal)}</span>
            </div>
            <div className="ot-preview-row ot-preview-strong">
              <span>Bleibt der Firma</span>
              <span>{fmtEur(draft.remaining)}</span>
            </div>
            <p className="ot-preview-note">{taxInfo.note}</p>
            {draft.remaining < 0 && (
              <p className="ot-warn">Die Provisionen übersteigen den Betrag – das erzeugt eine negative Marge.</p>
            )}
            {form.id && fromDateInput(form.dueDate) <= Date.now() && (
              <p className="ot-warn ot-warn-soft">
                Der Fälligkeitstag ist erreicht – falls die Rechnung schon gestellt oder bezahlt ist,
                bleiben ihre Beträge unverändert. Änderungen greifen nur, solange sie offen ist.
              </p>
            )}
          </div>

          <div className="ot-form-actions">
            <button type="button" className="ot-btn ot-btn-ghost" onClick={closeForm}>Abbrechen</button>
            <button type="button" className="ot-btn ot-btn-primary" disabled={!canSave} onClick={save}>
              {form.id ? 'Änderungen speichern' : 'Einmalzahlung anlegen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
